import { NavArrowLeft, NavArrowRight } from 'iconoir-react'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SegmentedControl } from 'app/components/action-center/SegmentedControl'
import { Button } from 'app/components/ui/button'
import { useIsMobile, useMediaQuery } from 'app/hooks/useMediaQuery'
import { dueDateAsDay } from 'app/lib/taskDueDate'
import { DAY_KEY, rangeTitle, TASK_CALENDAR_VIEWS, tasksByDay, visibleRange } from 'lib/taskCalendar'
import type { TaskJson } from 'app/types/entities'
import type { TaskCalendarView } from 'app/types/task-calendar'
import { TaskCalendarDay } from './TaskCalendarDay'
import { TaskCalendarMonth } from './TaskCalendarMonth'
import { TaskCalendarWeek } from './TaskCalendarWeek'
import { showQuickCreateTask } from 'app/lib/quickCreateTask'
import { TaskDetailSheet } from './TaskDetailSheet'
import { useCalendarTasks } from './useCalendarTasks'

const VIEW_LABELS: Record<TaskCalendarView, string> = { day: `Day`, week: `Week`, month: `Month` }

/** Google Calendar's single-key shortcuts. */
const VIEW_KEYS: Record<string, TaskCalendarView> = { d: `day`, w: `week`, m: `month` }
const NEXT_KEYS = new Set([`j`, `n`])
const PREVIOUS_KEYS = new Set([`k`, `p`])

/** View and date live in the URL (`cal`, `day`) so a calendar position bookmarks and survives refresh. */
export function TasksCalendar({
  query,
  refreshTrigger,
  onTotalChange,
  onContactClick,
  onTaskUpdated,
  onTaskTraversalItemsChange,
  highlightTaskId,
}: {
  query: string
  initialSort?: { sortString: string }
  refreshTrigger?: number
  onTotalChange?: (total: number) => void
  onContactClick?: (contactId: string, highlightTaskId?: string) => void
  onTaskUpdated?: () => void
  onTaskTraversalItemsChange?: (items: { taskId: string; contactId: string }[]) => void
  highlightTaskId?: string
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const isTouch = useMediaQuery(`(pointer: coarse)`)
  const [selectedTaskId, setSelectedTaskId] = useState(highlightTaskId)

  const calParam = searchParams.get(`cal`) as TaskCalendarView | null
  // A phone opens on the day list: a month grid cannot be read at 375px.
  const view: TaskCalendarView =
    calParam && TASK_CALENDAR_VIEWS.includes(calParam) ? calParam : isMobile ? `day` : `month`
  const dayParam = searchParams.get(`day`)
  const anchor = useMemo(() => {
    const parsed = dayParam ? moment(dayParam, DAY_KEY, true) : undefined
    return parsed?.isValid() ? parsed : moment().startOf(`day`)
  }, [dayParam])
  // Day loads its whole week: the phone's week strip dots every day that has tasks.
  const range = visibleRange(view === `day` ? `week` : view, anchor)

  const { tasks, undatedCount, isLoading, updateTaskFields, deleteTask, toggleStatus, reschedule } = useCalendarTasks({
    query,
    range,
    refreshTrigger,
    onTaskUpdated,
  })
  const byDay = useMemo(() => tasksByDay(tasks), [tasks])
  const selectedTask = tasks.find((t) => t.id === selectedTaskId)

  useEffect(() => onTotalChange?.(tasks.length), [tasks.length])
  useEffect(() => {
    onTaskTraversalItemsChange?.(
      tasks.flatMap((t) => (t.id && t.contactId ? [{ taskId: t.id, contactId: t.contactId }] : [])),
    )
  }, [tasks, onTaskTraversalItemsChange])

  const goTo = (next: { view?: TaskCalendarView; day?: moment.MomentInput }) =>
    setSearchParams((prev) => {
      if (next.view) prev.set(`cal`, next.view)
      if (next.day) prev.set(`day`, moment(next.day).format(DAY_KEY))
      return prev
    })
  const step = (direction: 1 | -1) => goTo({ day: anchor.clone().add(direction, view) })
  const openDay = (day: moment.MomentInput) => goTo({ view: `day`, day })

  const openCreate = (day: moment.MomentInput) =>
    showQuickCreateTask({ defaultDueDate: moment(day).toDate(), onCreated: () => onTaskUpdated?.() })
  const openTask = (task: TaskJson) => setSelectedTaskId(task.id)
  const rescheduleById = (taskId: string, day: moment.MomentInput) => {
    const task = tasks.find((t) => t.id === taskId)
    if (task && !(task.dueDate && dueDateAsDay(task.dueDate).isSame(day, `day`))) reschedule(task, day)
  }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable)) return
      const key = e.key.toLowerCase()
      // With the drawer open the keys walk tasks in date order and complete
      // them, matching the drawer's own hints and the table's behaviour.
      if (selectedTask) {
        const index = tasks.indexOf(selectedTask)
        const delta = key === `arrowdown` || key === `j` ? 1 : key === `arrowup` || key === `k` ? -1 : 0
        if (delta) setSelectedTaskId(tasks[Math.max(0, Math.min(tasks.length - 1, index + delta))]?.id)
        else if (key === `x`) toggleStatus(selectedTask)
        else return
        e.preventDefault()
        return
      }
      if (document.querySelector(`[role="dialog"]`)) return
      if (key === `t`) goTo({ day: moment() })
      else if (NEXT_KEYS.has(key)) step(1)
      else if (PREVIOUS_KEYS.has(key)) step(-1)
      else if (VIEW_KEYS[key]) goTo({ view: VIEW_KEYS[key] })
      else return
      e.preventDefault()
    }
    window.addEventListener(`keydown`, handler)
    return () => window.removeEventListener(`keydown`, handler)
  }, [view, anchor, selectedTask, tasks])

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <Button variant="white" size="sm" onClick={() => goTo({ day: moment() })} title="Today (T)">
            Today
          </Button>
          <Button variant="subtle" size="icon-sm" onClick={() => step(-1)} aria-label="Previous" title="Previous (K)">
            <NavArrowLeft />
          </Button>
          <Button variant="subtle" size="icon-sm" onClick={() => step(1)} aria-label="Next" title="Next (J)">
            <NavArrowRight />
          </Button>
          <h2 className="ml-1 truncate text-heading-xs text-strong" aria-live="polite">
            {rangeTitle(view, anchor)}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {undatedCount > 0 && (
            <span className="text-ui-xs">
              <Button
                variant="link"
                size="inline"
                onClick={() =>
                  setSearchParams((prev) => {
                    prev.delete(`view`)
                    return prev
                  })
                }
              >
                {undatedCount} with no due date
              </Button>
            </span>
          )}
          <SegmentedControl
            options={TASK_CALENDAR_VIEWS.map((v) => ({
              id: v,
              label: VIEW_LABELS[v],
              title: `${VIEW_LABELS[v]} (${v[0].toUpperCase()})`,
            }))}
            value={view}
            onSelect={(v) => goTo({ view: v })}
          />
        </div>
      </div>

      {/* Dims while a new range loads, so an empty-looking week is not mistaken for no tasks. */}
      <div
        data-tasks-calendar
        aria-busy={isLoading}
        className="relative flex min-h-0 flex-1 flex-col transition-opacity aria-busy:opacity-50"
      >
        {view === `month` && (
          <TaskCalendarMonth
            anchor={anchor}
            tasks={tasks}
            canDrag={!isTouch}
            onOpenTask={openTask}
            onToggleStatus={toggleStatus}
            onReschedule={reschedule}
            onCreate={openCreate}
            onOpenDay={openDay}
          />
        )}
        {view === `week` && (
          <TaskCalendarWeek
            anchor={anchor}
            tasksByDay={byDay}
            canDrag={!isTouch}
            onOpenTask={openTask}
            onToggleStatus={toggleStatus}
            onReschedule={rescheduleById}
            onCreate={openCreate}
            onOpenDay={openDay}
          />
        )}
        {view === `day` && (
          <TaskCalendarDay
            anchor={anchor}
            tasksByDay={byDay}
            onOpenTask={openTask}
            onToggleStatus={toggleStatus}
            onCreate={openCreate}
            onOpenDay={openDay}
          />
        )}
      </div>

      <TaskDetailSheet
        task={selectedTask}
        onClose={() => setSelectedTaskId(undefined)}
        onToggleStatus={toggleStatus}
        onUpdate={updateTaskFields}
        onDelete={deleteTask}
        onOpenContact={(task) => task.contactId && onContactClick?.(task.contactId, task.id)}
        onTaskCreated={() => onTaskUpdated?.()}
      />
    </div>
  )
}
